import { twMerge } from "tailwind-merge";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  H1,
  H3,
  H5,
  H6,
} from "@sellar-io/components";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useLocation } from "react-router";
import { FullPageSpinner, Icon, ProfilePicture } from "../../../components";
import { AddressFragment } from "../../../components/addressFragment/AddressFragment";
import { Alert } from "../../../components/alert/Alert";
import { useDashboardContext } from "../../../components/dashboard/Dashboard.shared";
import { ErrorMessage } from "../../../components/errorMessage/ErrorMessage";
import { NavigationBar } from "../../../components/navigation/NavigationBar";
import { CUSTOMER, BUYER, BUYERS } from "../../../constants/routes";
import { useBuyer } from "../../../hooks/api/v2/retailer/useBuyer";
import { useParamAsNumber } from "../../../hooks/useParamAsNumber";
import { ConnectionSendInvitationModal } from "../businessConnection/connectionSelector/ConnectionSendInvitationModal";
import { ConnectionStatusTag } from "../ConnectionStatusTag";
import { useNavigateToChild } from "../../../hooks/useParentNavigation";

export const BuyerPage = () => {
  const buyerId = useParamAsNumber(BUYER, "buyerId");

  const navigate = useNavigate();
  const navigateToChild = useNavigateToChild();

  const { isTwoPanel } = useDashboardContext();

  const buyerQuery = useBuyer(buyerId);

  const [invitationDialogActive, setInvitationDialogActive] =
    useState<boolean>(false);

  const location = useLocation();

  return (
    <>
      <NavigationBar
        title={buyerQuery.data?.Business.name ?? "Loading..."}
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={
          isTwoPanel ? undefined : () => navigate(BUYERS + location.search)
        }
      />
      {buyerQuery.isError ? (
        <ErrorMessage>{buyerQuery.error.message}</ErrorMessage>
      ) : buyerQuery.isLoading || !buyerQuery.data ? (
        <FullPageSpinner />
      ) : (
        <div className={"p-4"}>
          <div className={"flex flex-col gap-10 bg-white p-6"}>
            <div className={"flex flex-col gap-6"}>
              <ProfilePicture
                size={"2xl"}
                logoUrl={buyerQuery.data.Business.logoUrl}
                name={buyerQuery.data.Business.name}
                fallback="supplier-badge"
              />

              <div>
                <H1>{buyerQuery.data.Business.name}</H1>
                <H6>
                  {[
                    buyerQuery.data.Business.DeliveryAddress.city,
                    buyerQuery.data.Business.DeliveryAddress.region,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </H6>
              </div>
            </div>

            {buyerQuery.data.SupplierRetailerConnection?.id ? (
              <>
                <ConnectionStatusTag
                  connection={buyerQuery.data.SupplierRetailerConnection}
                />
                <Button
                  onClick={() => {
                    const connection =
                      buyerQuery.data.SupplierRetailerConnection;
                    if (!connection) return;

                    navigateToChild(
                      CUSTOMER.href({
                        connectionId: connection.id,
                      }),
                    );
                  }}
                >
                  View full profile
                </Button>
              </>
            ) : (
              <>
                {!!buyerQuery.data.Business.BusinessUsers.find(
                  (businessUser) => businessUser.role === "orderOnly",
                ) ? (
                  <Alert
                    message={"Please contact hello@sellar.io to request this."}
                    title={`Connections to ${buyerQuery.data.Business.name} must be approved manually by the owner`}
                  />
                ) : (
                  <Button
                    onClick={() => {
                      setInvitationDialogActive(true);
                    }}
                  >
                    <Icon icon={"HANDSHAKE"} />
                    Assign terms
                  </Button>
                )}
              </>
            )}
            <Card className="bg-white">
              <CardHeader
                className={twMerge(
                  "bg-white",
                  "flex items-center justify-between",
                )}
              >
                <H3>Contact info</H3>
              </CardHeader>
              <CardContent className={"grid grid-cols-2 gap-y-4"}>
                <div className={"flex flex-col"}>
                  <H5>Delivery address</H5>
                  <AddressFragment
                    address={buyerQuery.data.Business.DeliveryAddress}
                  />
                </div>
                <div className={"flex flex-col"}>
                  <H5>Website</H5>
                  <span>{buyerQuery.data.Business.websiteUrl ?? "N/A"}</span>
                </div>
                <div className={"flex flex-col"}>
                  <H5>Instagram</H5>
                  <span>{buyerQuery.data.Business.instagram ?? "N/A"}</span>
                </div>
                <div className={"flex flex-col"}>
                  <H5>Twitter</H5>
                  <span>{buyerQuery.data.Business.twitter ?? "N/A"}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
      <ConnectionSendInvitationModal
        active={invitationDialogActive}
        onSuccess={(connection) =>
          navigateToChild(CUSTOMER.href({ connectionId: connection.id }))
        }
        onDismiss={() => setInvitationDialogActive(false)}
      />
    </>
  );
};
